import React, { useMemo, useRef, useState } from 'react';
import {
  collectNotesInLasso,
  gridPitchesForKey,
  noteBlockForPitch,
  noteInVisualCell,
  noteRefKey,
  playheadOnVisualCell,
  visualSlotRange,
  visualSlotToBeatSub,
  visualSlotsPerMeasure,
  type MelodiaSessionConfig,
  type MelodyPitchId,
} from '../../lib/melody/melodiaBuilderConfig';
import { chordPitches, type BuildTool, type ChordDegreeId } from '../../lib/melody/melodiaKeys';

interface LassoPoint {
  measureIndex: number;
  visualSlot: number;
  pitchId: MelodyPitchId;
}

interface MelodiaGridZoneProps {
  config: MelodiaSessionConfig;
  activeStep: number;
  isPlaying: boolean;
  tool: BuildTool;
  activeChord: ChordDegreeId | null;
  selection: Set<string>;
  onSelectionChange: (keys: Set<string>) => void;
  onCellClick: (measureIndex: number, visualSlot: number, pitchId: MelodyPitchId) => void;
  selectedPitch: MelodyPitchId;
  onSelectPitch: (pitchId: MelodyPitchId) => void;
}

function gridColumns(measureCount: number, slotsPerMeas: number): string {
  return `3.75rem repeat(${measureCount * slotsPerMeas}, minmax(1.35rem, 1fr))`;
}

function laneStyle(block: ReturnType<typeof noteBlockForPitch>): React.CSSProperties {
  return {
    ['--lane-fill' as string]: block.fill,
    ['--lane-accent' as string]: block.color,
  } as React.CSSProperties;
}

function cellInLassoPreview(
  lasso: { anchor: LassoPoint; target: LassoPoint } | null,
  pitches: MelodyPitchId[],
  mi: number,
  visualSlot: number,
  pitchId: MelodyPitchId
): boolean {
  if (!lasso) return false;
  const miMin = Math.min(lasso.anchor.measureIndex, lasso.target.measureIndex);
  const miMax = Math.max(lasso.anchor.measureIndex, lasso.target.measureIndex);
  const vsMin = Math.min(lasso.anchor.visualSlot, lasso.target.visualSlot);
  const vsMax = Math.max(lasso.anchor.visualSlot, lasso.target.visualSlot);
  const pMin = Math.min(
    pitches.indexOf(lasso.anchor.pitchId),
    pitches.indexOf(lasso.target.pitchId)
  );
  const pMax = Math.max(
    pitches.indexOf(lasso.anchor.pitchId),
    pitches.indexOf(lasso.target.pitchId)
  );
  const pi = pitches.indexOf(pitchId);
  return mi >= miMin && mi <= miMax && visualSlot >= vsMin && visualSlot <= vsMax && pi >= pMin && pi <= pMax;
}

function cellIsSelected(
  selection: Set<string>,
  grid: MelodiaSessionConfig['grid'],
  mi: number,
  visualSlot: number,
  pitchId: MelodyPitchId,
  subdivision: MelodiaSessionConfig['subdivision']
): boolean {
  const { start, end } = visualSlotRange(visualSlot, subdivision);
  for (let si = start; si < end; si++) {
    if (selection.has(`${mi}:${si}`) && grid[mi]?.[si] === pitchId) return true;
  }
  return false;
}

export const MelodiaGridZone: React.FC<MelodiaGridZoneProps> = ({
  config,
  activeStep,
  isPlaying,
  tool,
  activeChord,
  selection,
  onSelectionChange,
  onCellClick,
  selectedPitch,
  onSelectPitch,
}) => {
  const slotsPerMeas = visualSlotsPerMeasure(config.timeSignature, config.subdivision);
  const pitches = gridPitchesForKey(config.keyId);
  const lassoActive = useRef(false);
  const [lasso, setLasso] = useState<{ anchor: LassoPoint; target: LassoPoint } | null>(null);

  const chordSet = useMemo(() => {
    if (!activeChord) return null;
    return new Set(chordPitches(config.keyId, activeChord));
  }, [activeChord, config.keyId]);

  const colTemplate = useMemo(
    () => gridColumns(config.measureCount, slotsPerMeas),
    [config.measureCount, slotsPerMeas]
  );

  const slotMarkers = useMemo(() => {
    const items: {
      mi: number;
      si: number;
      beatIndex: number;
      subIndex: number;
    }[] = [];
    for (let mi = 0; mi < config.measureCount; mi++) {
      for (let si = 0; si < slotsPerMeas; si++) {
        const { beatIndex, subIndex } = visualSlotToBeatSub(si, config.subdivision);
        items.push({ mi, si, beatIndex, subIndex });
      }
    }
    return items;
  }, [config.measureCount, slotsPerMeas, config.subdivision]);

  const finishLasso = (current: { anchor: LassoPoint; target: LassoPoint }) => {
    const refs = collectNotesInLasso(
      config.grid,
      pitches,
      config.subdivision,
      current.anchor,
      current.target
    );
    onSelectionChange(new Set(refs.map(noteRefKey)));
    setLasso(null);
    lassoActive.current = false;
  };

  const handlePointerDown = (
    e: React.PointerEvent,
    measureIndex: number,
    visualSlot: number,
    pitchId: MelodyPitchId
  ) => {
    if (isPlaying) return;
    if (tool !== 'lasso') return;
    e.preventDefault();
    lassoActive.current = true;
    const point = { measureIndex, visualSlot, pitchId };
    setLasso({ anchor: point, target: point });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerEnter = (
    measureIndex: number,
    visualSlot: number,
    pitchId: MelodyPitchId
  ) => {
    if (!lassoActive.current || tool !== 'lasso') return;
    setLasso((prev) =>
      prev ? { ...prev, target: { measureIndex, visualSlot, pitchId } } : prev
    );
  };

  const handlePointerUp = () => {
    if (!lassoActive.current || !lasso) return;
    finishLasso(lasso);
  };

  const handleCellClick = (measureIndex: number, visualSlot: number, pitchId: MelodyPitchId) => {
    if (isPlaying || lassoActive.current) return;
    if (tool === 'pencil') {
      onCellClick(measureIndex, visualSlot, pitchId);
    }
  };

  return (
    <div className="melodia-grid-zone flex-1 min-w-0 flex flex-col min-h-0">
      <div className="melodia-grid-scroll flex-1 min-h-0 overflow-auto eco-scroll-hidden">
        <div className="melodia-grid-table">
          <div
            className="melodia-grid-row melodia-grid-row--header"
            style={{ gridTemplateColumns: colTemplate }}
          >
            <div className="melodia-grid-gutter melodia-grid-gutter--header" aria-hidden />
            {Array.from({ length: config.measureCount }).map((_, mi) => (
              <div
                key={mi}
                className={`melodia-measure-band ${mi % 2 === 0 ? 'melodia-measure-band--a' : 'melodia-measure-band--b'}`}
                style={{ gridColumn: `span ${slotsPerMeas}` }}
              >
                Compás {mi + 1}
              </div>
            ))}
          </div>

          <div
            className="melodia-grid-row melodia-grid-row--ruler"
            style={{ gridTemplateColumns: colTemplate }}
          >
            <div className="melodia-grid-gutter melodia-grid-gutter--ruler">
              <span className="melodia-clef-mini">𝄞</span>
            </div>
            {slotMarkers.map(({ mi, si, beatIndex, subIndex }) => {
              const isBeatStart = subIndex === 0;
              const isOffbeat =
                config.subdivision >= 2 && subIndex === Math.floor(config.subdivision / 2);
              return (
                <div
                  key={`r-${mi}-${si}`}
                  className={[
                    'melodia-beat-ruler-cell',
                    isBeatStart ? 'melodia-beat-ruler-cell--downbeat' : 'melodia-beat-ruler-cell--sub',
                    mi > 0 && isBeatStart ? 'melodia-beat-ruler-cell--measure-start' : '',
                    subIndex === config.subdivision - 1 ? 'melodia-beat-ruler-cell--beat-end' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {isBeatStart && <span className="melodia-beat-tag">{beatIndex + 1}</span>}
                  {isOffbeat && (
                    <span className="melodia-offbeat-tag" aria-hidden>
                      &
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {pitches.map((pitchId) => {
            const block = noteBlockForPitch(pitchId);
            const laneSelected = selectedPitch === pitchId;
            const chordHint = chordSet?.has(pitchId) ?? false;

            return (
              <div
                key={pitchId}
                className={[
                  'melodia-grid-row melodia-lane-row',
                  laneSelected ? 'melodia-lane-row--selected' : '',
                  chordHint ? 'melodia-lane-row--chord' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ ...laneStyle(block), gridTemplateColumns: colTemplate }}
              >
                <button
                  type="button"
                  className="melodia-pitch-label"
                  style={laneStyle(block)}
                  onClick={() => onSelectPitch(pitchId)}
                  aria-label={`Seleccionar ${block.label}`}
                  aria-pressed={laneSelected}
                >
                  <span className="melodia-pitch-chip">{block.label}</span>
                </button>

                {slotMarkers.map(({ mi, si, beatIndex, subIndex }) => {
                  const cellPitch = noteInVisualCell(config.grid, mi, si, config.subdivision);
                  const isThisPitch = cellPitch === pitchId;
                  const occupiedOther = cellPitch != null && cellPitch !== pitchId;
                  const playhead = playheadOnVisualCell(activeStep, mi, si, config);
                  const downbeat = subIndex === 0;
                  const measureStart = mi > 0 && downbeat && beatIndex === 0;
                  const beatEnd = subIndex === config.subdivision - 1;
                  const selected = cellIsSelected(
                    selection,
                    config.grid,
                    mi,
                    si,
                    pitchId,
                    config.subdivision
                  );
                  const lassoPreview = cellInLassoPreview(lasso, pitches, mi, si, pitchId);

                  return (
                    <button
                      key={`${mi}-${si}-${pitchId}`}
                      type="button"
                      disabled={isPlaying}
                      onClick={() => handleCellClick(mi, si, pitchId)}
                      onPointerDown={(e) => handlePointerDown(e, mi, si, pitchId)}
                      onPointerEnter={() => handlePointerEnter(mi, si, pitchId)}
                      onPointerUp={handlePointerUp}
                      onPointerCancel={handlePointerUp}
                      className={[
                        'melodia-grid-cell',
                        downbeat ? 'melodia-grid-cell--downbeat' : 'melodia-grid-cell--sub',
                        beatEnd ? 'melodia-grid-cell--beat-end' : '',
                        measureStart ? 'melodia-grid-cell--measure-start' : '',
                        mi % 2 === 1 ? 'melodia-grid-cell--measure-alt' : '',
                        playhead ? 'melodia-grid-cell--playhead' : '',
                        isThisPitch ? 'melodia-grid-cell--filled' : '',
                        occupiedOther ? 'melodia-grid-cell--dimmed' : '',
                        laneSelected && !isThisPitch && !occupiedOther
                          ? 'melodia-grid-cell--lane-active'
                          : '',
                        selected ? 'melodia-grid-cell--selected' : '',
                        lassoPreview ? 'melodia-grid-cell--lasso' : '',
                        tool === 'lasso' ? 'melodia-grid-cell--lasso-mode' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-label={`${block.label}, compás ${mi + 1}, celda ${si + 1}`}
                      aria-selected={selected}
                    >
                      {isThisPitch && (
                        <span className="melodia-grid-note">
                          <span className="melodia-grid-note-head" aria-hidden />
                          <span className="melodia-grid-note-label">{block.label}</span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MelodiaGridZone;
